import React, { useCallback, useEffect, useState } from 'react';
import { IconFolder, IconArrowLeft, IconFiles, IconFileFilled } from '@tabler/icons-react';
import { useSalesforceViewModel } from '@features/contacts/hooks/useSalesforceViewModel.ts';
import { type ContactListDto } from '@features/contacts';
import { cn } from '@core-ui-lib/utils.ts';
import { Badge } from '@core-ui/Components/ui/badge.tsx';
import { useTranslation } from 'react-i18next';
import ContactsPreviewModal from '@features/wizard/components/ContactsPreviewModal.tsx';
import useWizardStore from '../../store/useWizardStore.ts';
import { ContactsFallbackComponent } from '@features/contacts/components/ContactsFallbackComponent.tsx';
import { SearchInput } from '@ui/shared/searchInput.tsx';


export default function SalesforceLists (): React.JSX.Element {
  const { selectedContactList, setContactList, setSelectedFolder, selectedFolder } = useWizardStore();

  const { t } = useTranslation();
  const [folderSelected, setFolderSelected] = useState('');
  const { retrieveSalesforceLists, retrieveSalesforceReports, salesforceSearch, nonce } = useSalesforceViewModel();

  const [selectedReport, setSelectedReport] = useState<ContactListDto>();
  const [previewListId, setPreviewListId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [elementList, setElementList] = useState<ContactListDto[]>([]);

  const fetchSalesforceFolders = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    const folders = await retrieveSalesforceLists();
    if (folders !== undefined) {
      setElementList(folders);
    }
    setIsLoading(false);
  }, [retrieveSalesforceLists, elementList]);

  const fetchSalesforceReports = useCallback(async (folderName: string): Promise<void> => {
    setIsLoading(true);
    const reports = await retrieveSalesforceReports(folderName);
    if (reports !== undefined) {
      setElementList(reports);
    }
    setIsLoading(false);
  }, [retrieveSalesforceReports, folderSelected]);

  const cleanData = async (searchQuery: string): Promise<void> => {
    if (searchQuery === '' && folderSelected === '') {
      await fetchSalesforceFolders();
    }

    if (searchQuery === '' && folderSelected !== '') {
      await fetchSalesforceReports(folderSelected);
    }
  };

  const handleListSearch = async (searchQuery: string): Promise<void> => {
    setIsLoading(true);
    setSelectedReport({
      contactsCount: 0,
      createdAt: '',
      id: '',
      isFolder: false,
      nonce: '',
      organizationId: '',
      sequenceId: 0,
      title: '',
      type: 'CSV',
      updatedAt: '',
      warningsCount: 0
    });

    if (searchQuery === '') {
      void cleanData(searchQuery);
      return;
    }

    try {
      const lists = await salesforceSearch(searchQuery);
      setElementList(lists ?? []);
    } finally {
      setIsLoading(false);
    }

    if (folderSelected !== '') {
      setIsLoading(true);
      const lists = await retrieveSalesforceReports(folderSelected);
      const filteredData = (lists ?? []).filter((list) =>
        list.title.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setIsLoading(false);
      setElementList(filteredData);
    }
  };

  useEffect(() => {
    if (selectedFolder !== '') {
      void fetchSalesforceReports(selectedFolder);

      setFolderSelected(selectedFolder);

      if (selectedContactList !== null) {
        setSelectedReport(selectedContactList);
      }
    } else {
      void fetchSalesforceFolders();
    }
  }, []);

  const handleSelectFolder = (folder: ContactListDto): void => {
    setFolderSelected(folder.title);
    setSelectedFolder(folder.title);
    void fetchSalesforceReports(folder.title);
    setSearchQuery('');
  };

  const handleSelectReports = (report: ContactListDto): void => {
    setSelectedReport(report);
    setContactList(report);
  };

  function resetSearchingState  (): void  {
    void cleanData('');
    setSearchQuery('');
  }

  const handleBack = (): void => {
    void fetchSalesforceFolders();
    setFolderSelected('');
    setSelectedFolder('');
    setSearchQuery('');
  };

  return (
    <>
      { folderSelected !== '' &&
        <div className="p-4 mb-4 rounded-md bg-blue-250 border border-blue-200">
          <div className="flex items-center gap-2 text-blue-400">
            <button
              type="button"
              className="flex items-center justify-center rounded-md p-1 hover:bg-blue-200 transition-colors"
              onClick={()=> {
                handleBack();
              }}
            >
              <IconArrowLeft className="h-4 w-4" />
            </button>

            <IconFolder stroke={0.5} className="h-4 w-4" />
            <span className="font-medium">{`${t('contacts.table.folderName')}: ${folderSelected}`}</span>
          </div>
        </div>
      }

      <SearchInput
        disabled={isLoading}
        value={searchQuery}
        onChange={(e) => {
          setSearchQuery(e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            void handleListSearch(String(searchQuery));
          }
        }}
        onClick={() => {
          void handleListSearch(String(searchQuery));
        }}
        onClear={() => {
          resetSearchingState();
        }}
        placeholder={(selectedFolder !== '') ? t('wizard.selectContacts.searchPh') : t('wizard.selectContacts.searchFolderPh')}
      />

      {isLoading || elementList.length === 0 ?
        <ContactsFallbackComponent
          verticalPadding={30}
          message={t( isLoading ? 'contacts.table.loadingReports' : 'contacts.table.emptyFolder')}
        >
          <IconFiles stroke={0.5} className="w-10 h-10 text-gray-500"/>
        </ContactsFallbackComponent>
        :
        <div className={`${(folderSelected === '') ? 'border-0' : ''} rounded-lg border border-border`}>
          {elementList.map((list: ContactListDto) => (
            <button
              key={list.id}
              type="button"
              onClick={() => {
                if (list.isFolder === true) {
                  handleSelectFolder(list);
                } else {
                  handleSelectReports(list);
                }
              }}
              className={cn(
                'w-full flex items-start gap-4 p-4 text-left transition-colors hover:bg-muted/40 border-b border-border/60 last:border-b-0',
                selectedReport?.id === list.id && 'bg-muted/60',
              )}
            >
              <div
                className={cn(
                  'mt-1 h-5 w-5 rounded-full border-2 flex-shrink-0 transition-colors',
                  selectedReport?.id === list.id
                    ? 'border-primary bg-primary'
                    : 'border-muted-foreground',
                )}
              >
                {selectedReport?.id === list.id && (
                  <div className="h-full w-full flex items-center justify-center">
                    <div className="h-2 w-2 rounded-full bg-white" />
                  </div>
                )}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  {(list?.isFolder) === true
                    ? <IconFolder stroke={0.5} />
                    : <IconFileFilled stroke={0.5} />}<span className="font-medium">{list.title}</span>
                </div>
                <div className="mt-2 flex items-center gap-4 text-sm">
                  {list.isFolder === false && <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setPreviewListId( list.id);
                    }}
                    className="text-primary hover:underline disabled:opacity-50 disabled:text-muted disabled:hover:no-underline transition-colors"
                  >
                    {t('wizard.selectContacts.viewContacts')}
                  </button>}
                </div>
              </div>

              {selectedReport?.id === list.id && (
                <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                  {t('wizard.selected')}
                </Badge>
              )}
            </button>
          ))}
        </div>
      }

      <ContactsPreviewModal
        isOpen={previewListId !== ''}
        onOpenChange={(open) => {
          if (!open) {
            setPreviewListId('');
          }
        }}
        contactListId={previewListId}
        contactListTitle={selectedReport?.title !== undefined ? selectedReport.title : ''}
        activeTab={'salesforce'}
        salesforceNonce={nonce}
      />
    </>
  );
}